import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { GameContext } from '../core/context';
import type { BodyTagData } from '../core/events';
import type { V2 } from '../core/math';
import type { Rng } from '../core/rng';
import { instancedDepthMaterial } from '../render/instancedDepth';
import { CulledInstances, cullView } from '../render/models/culledInstances';
import { modelPack } from '../render/models/modelLibrary';
import { tuneMeshyMaterial } from '../render/models/tune';
import { onQualityTier } from '../render/qualityTier';
import type { System } from '../core/system';
import type { Body } from '../physics/body';
import { Layer, type Shape } from '../physics/types';
import type { CityLayout } from '../world/types';

/**
 * Light street props that get knocked flying: dustbins, crates, traffic cones, bollards, roadworks
 * barriers. Physics bodies (asleep until touched) rendered with one view-culled InstancedMesh per kind
 * (render/models/culledInstances.ts: only the props in view are drawn). On MEDIUM and HIGH, when the street pack
 * loaded, each kind with a Meshy model (MESHY_MODEL: the dustbin, crate, barrel, cone and roadworks barrier) draws
 * it in place of the procedural shape; LOW keeps the procedural shapes and draws barrels as bins, as before
 * barrels had a kind of their own (no extra draw call on LOW).
 *
 * Placement: the world can list props in `layout.props`; if it doesn't, a deterministic default
 * scatter along the pavements is used so the street isn't bare.
 * Owner: game-director (src/props).
 */
export type PropKind = 'bin' | 'crate' | 'cone' | 'bollard' | 'barrier' | 'barrel';

export interface PropPlacement {
  kind: PropKind;
  pos: V2;
  heading?: number;
}

declare module '../world/types' {
  interface CityLayout {
    /** Optional authored prop placements (see src/props). */
    props?: PropPlacement[];
  }
}

export interface Prop {
  readonly id: number;
  readonly kind: PropKind;
  readonly body: Body;
  knocked: boolean;
}

export interface PropService {
  spawn(kind: PropKind, pos: V2, heading?: number): Prop;
  all(): readonly Prop[];
  /** Put every prop back where it started (mission restarts). */
  resetAll(): void;
}

declare module '../core/services' {
  interface ServiceMap {
    props: PropService;
  }
}

interface KindDef {
  shape: Shape;
  mass: number;
  /** Visual height used for the tip-over pivot. */
  height: number;
  /** Speed (m/s) above which a hit knocks it over. */
  knockSpeed: number;
  color: number;
  debris: number | null;
  build(): THREE.BufferGeometry;
  max: number;
}

/** Cull sphere per kind (m): the prop's own size plus a margin for its shadow falling into view. */
const CULL_RADIUS: Record<PropKind, number> = { bin: 2, crate: 2, cone: 1.6, bollard: 2, barrier: 2.6, barrel: 2 };

/** The street-pack model each kind draws on MEDIUM and HIGH (docs/research/MESHY-WORLD-PLAN.md, Tier 1 and 2). */
const MESHY_MODEL: Partial<Record<PropKind, string>> = { bin: 'dustbin', crate: 'crate', barrel: 'barrel', cone: 'cone', barrier: 'roadworks-barrier', bollard: 'bollard' };

function nonIndexed(g: THREE.BufferGeometry): THREE.BufferGeometry {
  return g.index ? g.toNonIndexed() : g;
}
function cyl(rt: number, rb: number, h: number, y: number, seg = 10): THREE.BufferGeometry {
  return nonIndexed(new THREE.CylinderGeometry(rt, rb, h, seg).translate(0, y, 0));
}
function bx(w: number, h: number, d: number, x: number, y: number, z: number): THREE.BufferGeometry {
  return nonIndexed(new THREE.BoxGeometry(w, h, d).translate(x, y, z));
}

const KINDS: Record<PropKind, KindDef> = {
  bin: {
    shape: { type: 'circle', r: 0.32 },
    mass: 22,
    height: 0.95,
    knockSpeed: 1.5,
    color: 0x6f7470,
    debris: 0x4a4a40,
    build: () => mergeGeometries([cyl(0.3, 0.27, 0.85, 0.425, 12), cyl(0.33, 0.33, 0.08, 0.89, 12), cyl(0.06, 0.06, 0.08, 0.97, 6)])!,
    max: 220,
  },
  crate: {
    shape: { type: 'box', hx: 0.36, hz: 0.36 },
    mass: 18,
    height: 0.72,
    knockSpeed: 1.5,
    color: 0x8a6a42,
    debris: 0x6b4f2e,
    build: () =>
      mergeGeometries([
        bx(0.72, 0.72, 0.72, 0, 0.36, 0),
        bx(0.76, 0.08, 0.76, 0, 0.1, 0),
        bx(0.76, 0.08, 0.76, 0, 0.62, 0),
      ])!,
    max: 160,
  },
  cone: {
    shape: { type: 'circle', r: 0.2 },
    mass: 3,
    height: 0.7,
    knockSpeed: 0.8,
    color: 0xe0601c,
    debris: null,
    build: () => mergeGeometries([cyl(0.03, 0.17, 0.66, 0.37, 10), bx(0.42, 0.05, 0.42, 0, 0.025, 0)])!,
    max: 160,
  },
  bollard: {
    shape: { type: 'circle', r: 0.15 },
    mass: 90,
    height: 0.95,
    knockSpeed: 6,
    color: 0x22252a,
    debris: 0x22252a,
    build: () => mergeGeometries([cyl(0.13, 0.15, 0.85, 0.425, 10), cyl(0.15, 0.15, 0.1, 0.9, 10)])!,
    max: 260,
  },
  barrier: {
    shape: { type: 'box', hx: 0.9, hz: 0.18 },
    mass: 14,
    height: 1.0,
    knockSpeed: 1,
    color: 0xd8d2c0,
    debris: 0xc23a22,
    build: () =>
      mergeGeometries([
        bx(1.8, 0.22, 0.05, 0, 0.82, 0),
        bx(0.06, 1.0, 0.36, -0.82, 0.5, 0),
        bx(0.06, 1.0, 0.36, 0.82, 0.5, 0),
      ])!,
    max: 80,
  },
  barrel: {
    shape: { type: 'circle', r: 0.36 },
    mass: 40,
    height: 0.9,
    knockSpeed: 1.5,
    color: 0x6b4a2c,
    debris: 0x4a3220,
    build: () => mergeGeometries([cyl(0.36, 0.31, 0.45, 0.225, 12), cyl(0.31, 0.36, 0.45, 0.675, 12)])!,
    max: 80,
  },
};

interface PropEntity extends Prop {
  home: V2;
  homeHeading: number;
  /** Visual tilt state. */
  tilt: number;
  tiltTarget: number;
  tiltAxis: number;
  hop: number;
  hopV: number;
  instance: number;
}

/** Default scatter if the layout doesn't author props. */
export function defaultPropPlacements(layout: CityLayout, rng: Rng): PropPlacement[] {
  const out: PropPlacement[] = [];
  for (const path of layout.pedPaths) {
    const pts = path.points;
    if (pts.length < 2) continue;
    let cx = 0;
    let cz = 0;
    for (const p of pts) {
      cx += p.x;
      cz += p.z;
    }
    cx /= pts.length;
    cz /= pts.length;
    const n = path.loop ? pts.length : pts.length - 1;
    for (let i = 0; i < n; i++) {
      const a = pts[i]!;
      const b = pts[(i + 1) % pts.length]!;
      const len = Math.hypot(b.x - a.x, b.z - a.z);
      if (len < 6) continue;
      const dx = (b.x - a.x) / len;
      const dz = (b.z - a.z) / len;
      // Normal pointing away from the block centre = toward the kerb.
      let nx = -dz;
      let nz = dx;
      const mx = (a.x + b.x) / 2 - cx;
      const mz = (a.z + b.z) / 2 - cz;
      if (nx * mx + nz * mz < 0) {
        nx = -nx;
        nz = -nz;
      }
      // Bollards guarding the corner.
      for (const t of [2.2, 3.6]) out.push({ kind: 'bollard', pos: { x: a.x + dx * t + nx * 1.1, z: a.z + dz * t + nz * 1.1 } });
      // Bins and crates against the building line.
      const count = Math.floor(len / 16);
      for (let k = 1; k <= count; k++) {
        const t = (k / (count + 1)) * len + rng.range(-2, 2);
        const kind: PropKind = rng.chance(0.6) ? 'bin' : 'crate';
        const p = { x: a.x + dx * t - nx * 1.0, z: a.z + dz * t - nz * 1.0 };
        out.push({ kind, pos: p, heading: rng.range(0, Math.PI) });
        if (kind === 'crate' && rng.chance(0.4)) out.push({ kind: 'crate', pos: { x: p.x + dx * 0.85, z: p.z + dz * 0.85 }, heading: rng.range(0, Math.PI) });
      }
    }
  }
  // A couple of roadworks: cones + a barrier across one lane.
  const edges = layout.roads.edges.filter((e) => !e.alley);
  for (let r = 0; r < Math.min(3, edges.length); r++) {
    const e = rng.pick(edges);
    const a = layout.roads.nodes[e.from]!.pos;
    const b = layout.roads.nodes[e.to]!.pos;
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    const dx = (b.x - a.x) / len;
    const dz = (b.z - a.z) / len;
    const lx = dz;
    const lz = -dx;
    const t0 = len * rng.range(0.35, 0.55);
    const side = rng.chance(0.5) ? 1 : -1;
    const off = side * e.laneWidth * 0.5;
    for (let k = 0; k < 5; k++) {
      const t = t0 + k * 2.2;
      out.push({ kind: 'cone', pos: { x: a.x + dx * t + lx * (off + side * (k === 0 || k === 4 ? 0.6 : 1.4)), z: a.z + dz * t + lz * (off + side * (k === 0 || k === 4 ? 0.6 : 1.4)) } });
    }
    out.push({ kind: 'barrier', pos: { x: a.x + dx * (t0 + 4.4) + lx * off * 1.2, z: a.z + dz * (t0 + 4.4) + lz * off * 1.2 }, heading: Math.atan2(lx, lz) });
  }
  return out;
}

export function createPropSystem(): System {
  let ctx: GameContext;
  let rng: Rng;
  const props: PropEntity[] = [];
  const byBody = new Map<number, PropEntity>();
  const batches = new Map<PropKind, CulledInstances>();
  const procedural = new Map<PropKind, { geo: THREE.BufferGeometry; mat: THREE.Material }>();
  const meshy = new Map<PropKind, { geo: THREE.BufferGeometry; mat: THREE.Material }>();
  /** Barrels draw through the bin batch (slots after the bins' own) on LOW or without a Meshy barrel. */
  let barrelsAsBins = true;
  let unsubTier: (() => void) | null = null;
  const counts = new Map<PropKind, number>();
  let nextId = 1;
  const m4 = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const qTilt = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  const axis = new THREE.Vector3();
  const scale = new THREE.Vector3(1, 1, 1);
  const posV = new THREE.Vector3();
  const pose = { x: 0, z: 0, angle: 0 };

  function spawn(kind: PropKind, pos: V2, heading = 0): Prop {
    const def = KINDS[kind];
    const used = counts.get(kind) ?? 0;
    if (used >= def.max) throw new Error(`Prop pool for '${kind}' exhausted (${def.max})`);
    counts.set(kind, used + 1);
    const id = nextId++;
    const body = ctx.physics.createBody({
      shape: def.shape,
      pos,
      angle: heading,
      mass: def.mass,
      restitution: 0.3,
      friction: 0.5,
      linearDamping: 2.2,
      angularDamping: 3,
      layer: Layer.PROP,
      mask: Layer.ALL,
      kind: 'prop',
      entityId: id,
    });
    body.sleeping = true;
    const p: PropEntity = {
      id,
      kind,
      body,
      knocked: false,
      home: { x: pos.x, z: pos.z },
      homeHeading: heading,
      tilt: 0,
      tiltTarget: 0,
      tiltAxis: 0,
      hop: 0,
      hopV: 0,
      instance: used,
    };
    props.push(p);
    byBody.set(body.id, p);
    return p;
  }

  function knock(p: PropEntity, speed: number, other: BodyTagData | null): void {
    const def = KINDS[p.kind];
    if (!p.knocked) {
      p.knocked = true;
      p.tiltTarget = p.kind === 'barrier' ? Math.PI / 2 : Math.PI / 2 - 0.12;
      p.tiltAxis = rng.range(0, Math.PI * 2);
      // Knocked props only collide with the world, so cars plough through the clutter.
      p.body.layer = Layer.DEBRIS;
      p.body.mask = Layer.STATIC;
    }
    p.hopV = Math.max(p.hopV, Math.min(7, speed * 0.35));
    const player = ctx.services.tryGet('player');
    const byPlayer = !!other && (other.kind === 'player' || (other.kind === 'vehicle' && player?.vehicle?.id === other.id));
    ctx.events.emit('propHit', { propId: p.id, kind: p.kind, pos: { x: p.body.pos.x, z: p.body.pos.z }, speed, byPlayer });
    const particles = ctx.services.tryGet('particles');
    if (particles && def.debris !== null && speed > 4) {
      particles.emit('debris', { x: p.body.pos.x, y: 0.5, z: p.body.pos.z }, { count: Math.min(10, Math.round(speed / 2)), color: def.debris, intensity: 0.6 });
    }
  }

  const service: PropService = {
    spawn,
    all: () => props,
    resetAll() {
      for (const p of props) {
        p.knocked = false;
        p.tilt = p.tiltTarget = 0;
        p.hop = p.hopV = 0;
        p.body.layer = Layer.PROP;
        p.body.mask = Layer.ALL;
        p.body.setPose(p.home, p.homeHeading);
        p.body.setVelocity({ x: 0, z: 0 }, 0);
        p.body.sleeping = true;
      }
    },
  };

  return {
    id: 'props',
    order: 460,
    init(c) {
      ctx = c;
      rng = c.rng.fork('props');
      c.services.provide('props', service);
      for (const [kind, def] of Object.entries(KINDS) as [PropKind, KindDef][]) {
        const geo = def.build();
        const mat = new THREE.MeshStandardMaterial({ color: def.color, roughness: kind === 'cone' ? 0.6 : 0.8, metalness: kind === 'bin' ? 0.5 : 0.05 });
        // View-culled (render/models/culledInstances.ts): only the props in this frame's view are drawn.
        const batch = new CulledInstances(geo, mat, kind === 'bin' ? def.max + KINDS.barrel.max : def.max, CULL_RADIUS[kind]);
        batch.mesh.name = `props-${kind}`;
        batch.mesh.castShadow = true;
        // Own depth material: keeps three from re-fetching the shared one's program at every plain-mesh /
        // instanced flip of the shadow pass (render/instancedDepth.ts).
        batch.mesh.customDepthMaterial = instancedDepthMaterial(false);
        batch.mesh.receiveShadow = true;
        batches.set(kind, batch);
        procedural.set(kind, { geo, mat });
        c.scene.add(batch.mesh);
      }
      // The Meshy models (street pack) replace their kinds on MEDIUM and HIGH; LOW keeps the procedural shapes.
      const pack = c.render ? modelPack('street') : null;
      for (const [kind, id] of Object.entries(MESHY_MODEL) as [PropKind, string][]) {
        const model = pack?.get(id);
        if (!model) continue;
        // baked to vertex colours (rgba: colour + tint weight; the alpha is not used here) or textured from the atlas
        const baked = model.geometry.getAttribute('color')?.itemSize === 4;
        const look = kind === 'bin' ? { roughness: 0.62, metalness: 0.3, envMapIntensity: 0.9 } : kind === 'cone' ? { roughness: 0.6 } : {};
        meshy.set(kind, { geo: model.geometry, mat: tuneMeshyMaterial(new THREE.MeshStandardMaterial(baked ? {} : { map: model.texture }), { ...look, vertexColors: baked }) });
      }
      if (meshy.size) {
        unsubTier = onQualityTier((t) => {
          for (const [kind, m] of meshy) {
            const b = batches.get(kind)!;
            const use = t === 'low' ? procedural.get(kind)! : m;
            b.mesh.geometry = use.geo;
            b.mesh.material = use.mat;
          }
          const asBins = t === 'low' || !meshy.has('barrel');
          if (asBins !== barrelsAsBins) {
            // move every barrel to the other batch: hide it in both, the next update shows it in the right one
            for (const p of props) {
              if (p.kind !== 'barrel') continue;
              batches.get('bin')!.set(KINDS.bin.max + p.instance, null);
              batches.get('barrel')!.set(p.instance, null);
            }
            barrelsAsBins = asBins;
          }
        });
      }
      c.hooks.registerSnapshot('props', () => ({ count: props.length, knocked: props.filter((p) => p.knocked).length }));
    },
    start() {
      const layout = ctx.services.get('layout');
      const placements = layout.props ?? defaultPropPlacements(layout, rng);
      for (const pl of placements) {
        if (!ctx.physics.isShapeFree(KINDS[pl.kind].shape, pl.pos, pl.heading ?? 0, Layer.ALL)) continue;
        try {
          spawn(pl.kind, pl.pos, pl.heading ?? 0);
        } catch {
          break;
        }
      }
    },
    fixedUpdate() {
      for (const c of ctx.physics.contacts) {
        const pa = c.a.kind === 'prop' ? byBody.get(c.a.bodyId) : undefined;
        const pb = c.b && c.b.kind === 'prop' ? byBody.get(c.b.bodyId) : undefined;
        for (const [p, other] of [
          [pa, c.b],
          [pb, c.a],
        ] as const) {
          if (!p || !other || other.kind === 'static') continue;
          const sp = Math.max(c.speed, p.body.speed());
          if (c.isNew && sp >= KINDS[p.kind].knockSpeed) knock(p, sp, other);
        }
      }
    },
    update(dt, alpha) {
      for (const p of props) {
        const def = KINDS[p.kind];
        // Visual hop + tip-over.
        if (p.hopV > 0 || p.hop > 0) {
          p.hopV -= 20 * dt;
          p.hop = Math.max(0, p.hop + p.hopV * dt);
          if (p.hop === 0 && p.hopV < 0) p.hopV = 0;
        }
        p.tilt += (p.tiltTarget - p.tilt) * Math.min(1, dt * 9);
        p.body.interpolate(alpha, pose);
        q.setFromAxisAngle(up, pose.angle);
        if (p.tilt > 0.001) {
          axis.set(Math.cos(p.tiltAxis), 0, Math.sin(p.tiltAxis));
          qTilt.setFromAxisAngle(axis, p.tilt);
          q.premultiply(qTilt);
        }
        const lie = p.knocked ? (def.shape.type === 'circle' ? def.shape.r : 0.18) : 0;
        posV.set(pose.x, p.hop + lie * Math.sin(p.tilt) * 0.6, pose.z);
        m4.compose(posV, q, scale);
        if (p.kind === 'barrel' && barrelsAsBins) batches.get('bin')!.set(KINDS.bin.max + p.instance, m4);
        else batches.get(p.kind)!.set(p.instance, m4);
      }
      // With a renderer the render system packs the batches with the frame's frustum; headless, pack them all.
      if (!cullView.on) for (const b of batches.values()) b.flush(null);
    },
    dispose() {
      unsubTier?.();
      for (const b of batches.values()) b.dispose();
      for (const { geo, mat } of procedural.values()) {
        geo.dispose();
        mat.dispose();
      }
      // the Meshy models' geometry and texture belong to the street pack; only their materials are ours
      for (const m of meshy.values()) m.mat.dispose();
    },
  };
}
